import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { BRAND, IMAGES, HERO_DESCRIPTION } from "../lib/content";
import { RevealLines, FadeUp } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { CornerWebs } from "../components/CornerWebs";
import { EditorialMarquee } from "../components/EditorialMarquee";
import { CastingJoinSection } from "../components/CastingJoinSection";
import { FlameInitial } from "../components/FlameInitial";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.9], [1, 0]);
  const scrollHintOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  return (
    <section ref={ref} data-testid="hero" className="relative flex min-h-screen flex-col items-center justify-start overflow-hidden px-6 pt-20 pb-24 md:pt-10">
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
        <img
          src="/brand/purple-smoke.jpg"
          alt=""
          aria-hidden="true"
          className="animate-smoke h-full w-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-ink/50" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-transparent to-ink" />
      </motion.div>

      <div className="pointer-events-none absolute left-1/2 top-1/2 z-0 h-[620px] w-[620px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-25 blur-[150px]" style={{ backgroundColor: "#7c3aed" }} />
      <CornerWebs />

      <motion.div style={{ y: contentY, opacity: fade }} className="relative z-10 mx-auto max-w-4xl text-center">
        <motion.div
          data-testid="hero-logo-unit"
          className="mx-auto w-full max-w-2xl"
        >
          <div className="relative">
            <motion.img
              src={BRAND.wordmark}
              alt="Tattooed Abroad"
              initial={{ opacity: 0, scale: 1.08, filter: "blur(12px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              transition={{ duration: 1.4, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="block w-full"
            />
          </div>
          <div className="mx-auto -mt-3 w-[78%] md:translate-x-[31px]">
            <motion.div
              role="img"
              aria-label="Journeys Written in Skin"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.7 }}
              className="w-full"
              style={{
                aspectRatio: "695 / 130",
                background: "linear-gradient(90deg, #FFD700 0%, #FF8A00 48%, #E2341E 100%)",
                WebkitMaskImage: "url(/brand/journeys-written-in-skin-script-trim.png)",
                maskImage: "url(/brand/journeys-written-in-skin-script-trim.png)",
                WebkitMaskSize: "contain",
                maskSize: "contain",
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskPosition: "center",
                maskPosition: "center",
              }}
            />
          </div>
        </motion.div>

        <div className="mx-auto md:translate-x-[28px]">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.95 }}
          className="mx-auto mt-10 max-w-3xl text-balance font-body text-base leading-relaxed text-white/70 md:text-lg"
        >
          {HERO_DESCRIPTION}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.15 }}
          className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row"
        >
          <Link
            to="/support"
            data-testid="hero-kickstarter-cta"
            className="group inline-flex items-center gap-3 border border-flame bg-flame px-8 py-4 font-body text-xs uppercase tracking-[0.2em] text-ink transition-colors hover:bg-ember"
          >
            Follow the Kickstarter
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/series"
            data-testid="hero-series-cta"
            className="group inline-flex items-center gap-3 border border-white/25 px-8 py-4 font-body text-xs uppercase tracking-[0.2em] text-white transition-colors hover:border-flame hover:text-flame"
          >
            Discover the Series
            <ArrowDown size={16} className="transition-transform group-hover:translate-y-1" />
          </Link>
        </motion.div>
        </div>
      </motion.div>

      <motion.div
        style={{ opacity: scrollHintOpacity }}
        className="pointer-events-none absolute bottom-6 left-1/2 z-10 -translate-x-1/2"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="font-body text-[10px] uppercase tracking-[0.3em] text-white/40">Scroll</span>
          <div className="h-10 w-px animate-pulse bg-gradient-to-b from-flame to-transparent" />
        </div>
      </motion.div>
    </section>
  );
};

const SeriesTeaser = () => (
  <section data-testid="home-series-teaser" className="relative overflow-hidden pt-4 pb-16 md:pt-6 md:pb-24">
    <div className="mx-auto grid max-w-7xl items-start gap-10 px-6 md:grid-cols-2 md:px-10">
      <div className="relative overflow-hidden">
        <div className="pointer-events-none absolute left-0 top-1/2 h-[440px] w-[560px] max-w-full -translate-y-1/2 rounded-full bg-flame opacity-[0.14] blur-[150px]" />
        <div className="relative z-10">
          <FadeUp>
            <p className="font-gothic text-xs uppercase tracking-[0.35em] text-flame">The Series</p>
          </FadeUp>
        <h2 className="mt-4 font-display text-4xl font-light leading-[1.12] tracking-tight text-white md:text-6xl">
          <RevealLines
            lines={[
              <>
                <img
                  src="/brand/ta-letter-e.png"
                  alt="E"
                  data-testid="series-headline-flame-e"
                  className="inline w-auto select-none"
                  style={{ height: "0.81em", verticalAlign: "baseline", marginRight: "-0.05em" }}
                />very tattoo begins
              </>,
              "long before the needle",
              "hits the skin.",
            ]}
          />
        </h2>
        <FadeUp delay={0.2}>
          <p className="mt-6 max-w-xl font-body text-lg leading-relaxed text-white/60">{"Tattooed Abroad is an international documentary web series that brings tattoo artists and clients from different countries together to create permanent works of art. Each episode follows the journeys that lead them to the same shop, the meaning behind the design, the artist’s creative process, and the trust that develops between them—using tattooing as a doorway into travel, culture, identity, personal transformation, and the lasting bond created through journeys written in skin."}</p>
          <div className="mt-7 flex flex-wrap items-center gap-x-8 gap-y-4">
            <Link
              to="/series"
              data-testid="home-explore-series"
              className="group inline-flex items-center gap-3 font-body text-xs uppercase tracking-[0.25em] text-flame"
            >
              Explore the concept
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/tattoo-times"
              data-testid="home-tattoo-times"
              className="group inline-flex items-center gap-3 font-body text-xs uppercase tracking-[0.25em] text-white/70 transition-colors hover:text-flame"
            >
              Read Tattoo Times
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </FadeUp>
        </div>
      </div>

      <FadeUp delay={0.15}>
        <div className="relative">
          <div className="absolute -inset-3 -z-10 bg-flame/10 blur-2xl" />
          <img src={IMAGES.tattooProcess} alt="Tattoo in process" className="aspect-[4/5] w-full border border-white/10 object-cover grayscale-[30%]" />
          <img src={BRAND.web} alt="" className="absolute -right-4 -top-4 w-16 opacity-60" />
        </div>
      </FadeUp>
    </div>
  </section>
);

const SupportTeaser = () => (
  <section data-testid="home-support-teaser" className="relative overflow-hidden pt-12 pb-24 md:pt-16 md:pb-28">
    <div className="absolute inset-0 z-0 bg-ink">
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-flame opacity-20 blur-[130px]" />
    </div>
    <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
      <FadeUp>
        <p className="font-gothic text-sm font-medium uppercase tracking-[0.35em] text-fire [text-shadow:0_0_16px_rgba(226,52,30,0.35)] md:text-base">The First Journey Starts Here</p>
        <h2 className="mt-6 font-display text-5xl font-light leading-tight tracking-tight text-white md:text-7xl">
          <FlameInitial letter="H" size="0.88em" className="mr-[-0.01em] translate-y-[3px] scale-x-135" />elp us make<br />
          <span
            className="italic"
            style={{
              backgroundImage: "linear-gradient(90deg, #FFD700 0%, #FF7A1A 50%, #E2341E 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            Season One.
          </span>
        </h2>
        <p className="mx-auto mt-6 max-w-xl font-body text-lg leading-relaxed text-white/80">
          Tattooed Abroad will launch through a 30-day Kickstarter campaign backing the complete
          11-episode first season.
        </p>
        <div className="mt-10 flex items-center justify-center gap-5">
          <img src={BRAND.star} alt="" aria-hidden="true" className="h-9 w-9 opacity-90 md:h-11 md:w-11" />
          <Link
            to="/support"
            data-testid="home-support-cta"
            className="inline-flex items-center gap-3 border border-flame px-10 py-5 font-body text-sm uppercase tracking-[0.2em] text-white transition-colors hover:bg-flame hover:text-ink"
          >
            Kickstarter — Coming Soon
          </Link>
          <img src={BRAND.star} alt="" aria-hidden="true" className="h-9 w-9 opacity-90 md:h-11 md:w-11" />
        </div>
      </FadeUp>
    </div>
  </section>
);

const JourneySection = () => <CastingJoinSection source="home" />;

export default function Home() {
  return (
    <>
      <Seo
        title="Tattooed Abroad | Tattoos, Travel & Personal Stories"
        description="Discover Tattooed Abroad, an upcoming documentary series exploring tattoos, travel, and the personal stories connecting artists and clients across cultures."
      />
      <Hero />
      <SeriesTeaser />
      <EditorialMarquee />
      <SupportTeaser />
      <JourneySection />
    </>
  );
}
